// Phase 4 Plan Task 7 — grow-rows resolution logic.
//
// Pure helper extracted from MatchScreen so it can be unit-tested without
// mounting the full component (which requires Pixi + provider tree).
//
// resolveGrowRows answers: given the current state, what should
// `--grow-rows` be set to right now?
//
// Rules (checked in order):
//   - reducedMotion: snap immediately → return boardRowCount.
//   - epoch increased (newEpoch > prevEpoch): the floor-drop beat just fired;
//     the CSS transition is active → return boardRowCount to animate it. This
//     is the primary, *animated* grow path — the reveal cinematic pulses the
//     epoch and the box "breathes" open over 500 ms.
//   - board grew PAST the displayed rows AND no grow cinematic is in flight
//     (growCinematicInFlight === false): snap immediately. The epoch will never
//     pulse for this grow — fast-bot WS skip, rejoin into a grown board, a
//     motion-gated reveal, or a reveal tap-skipped before its beat — so holding
//     would leave the back-rank cells off-screen under the emote bar
//     (ARCA-154 / GH #94). This is the SAFETY NET, deliberately gated so it
//     never fires while a cinematic is mid-animation (which would skip the
//     breathe-open in normal play).
//   - otherwise (steady state, OR grown while a cinematic is still animating):
//     hold the current growRows — let the floor-drop beat gate the animation.
//
// Callers also use this at first-mount to initialize growRows to boardRowCount
// with no prior transition — the @property initial-value is 6 which happens
// to equal the game-start row count, but a rejoining player might be at 8/10.
// The caller initializes growRows via useState(boardRowCount) and handledEpoch
// via useRef(boardReanchorEpoch) at first render. A rejoining player whose first
// non-null playerView is already grown fires no reveal cinematic, so the safety
// net snaps it up on the first effect run instead of holding it at the pre-view 6.

export interface ResolveGrowRowsArgs {
  /** Live row count from playerView (6 | 8 | 10 in practice). */
  boardRowCount: number
  /** Current growRows state value already applied to the element. */
  growRows: number
  /** The epoch value already handled by the previous effect run. */
  prevEpoch: number
  /** Latest $boardReanchor epoch value. */
  newEpoch: number
  /** $reducedMotion computed value. */
  reducedMotion: boolean
  /**
   * True while a grow cinematic (Flop/River reveal) is mounted and animating
   * THIS grow. When true the function HOLDS the smaller growRows so the
   * floor-drop epoch pulse animates the breathe-open; when false a board that
   * has grown past the displayed rows snaps immediately (fast-bot / rejoin /
   * motion-gated / post-skip — paths where the epoch never pulses). Without
   * this gate the snap would fire during a normal cinematic (boardRowCount
   * grows before the epoch pulses) and skip the 500 ms breathe-open animation.
   */
  growCinematicInFlight: boolean
}

export interface ResolveGrowRowsResult {
  /** The value to set growRows state to. */
  nextGrowRows: number
  /** The epoch value to store as "handled" after this resolution. */
  nextHandledEpoch: number
}

/**
 * Resolves the next `--grow-rows` value and the epoch to mark as handled.
 *
 * Returns the existing `growRows` unchanged when no transition should fire
 * (steady state, no epoch change, full-motion mode).
 */
export function resolveGrowRows({
  boardRowCount,
  growRows,
  prevEpoch,
  newEpoch,
  reducedMotion,
  growCinematicInFlight,
}: ResolveGrowRowsArgs): ResolveGrowRowsResult {
  if (reducedMotion) {
    // Snap immediately — CSS transition is disabled.
    return { nextGrowRows: boardRowCount, nextHandledEpoch: newEpoch }
  }
  if (newEpoch > prevEpoch) {
    // Floor-drop beat fired — CSS transition animates the breathe-open. This is
    // the primary animated path; it must be checked before the safety net so a
    // real pulse always wins and animates rather than snapping.
    return { nextGrowRows: boardRowCount, nextHandledEpoch: newEpoch }
  }
  if (boardRowCount > growRows && !growCinematicInFlight) {
    // ARCA-154 / GH #94 — the board grew past the displayed rows and NO reveal
    // cinematic is animating it (fast-bot skip, rejoin into a grown board,
    // motion-gated reveal, or a reveal tap-skipped before its floor-drop beat).
    // The epoch will never pulse for this grow, so holding would leave the
    // back-rank cells off-screen under the emote bar. Snap up. Math.max keeps
    // the handled epoch monotonic (the store never regresses in production, but
    // a snap must never roll the handled epoch backwards).
    return {
      nextGrowRows: boardRowCount,
      nextHandledEpoch: Math.max(prevEpoch, newEpoch),
    }
  }
  // Steady state, OR grown while a cinematic is still animating — hold the
  // current value so the floor-drop beat gates the breathe-open animation.
  return { nextGrowRows: growRows, nextHandledEpoch: prevEpoch }
}
